import { useEffect, useState } from 'react';
import { getMarketingPreference, setMarketingPreference } from '../../services/customer';

export default function MessagePreferences({ restaurantId, customerId, restaurantName, accentColor, isGuest, onSignIn }) {
  const [allowed, setAllowed] = useState(false);
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (isGuest || !restaurantId || !customerId) return;
    let active = true;
    setLoading(true);
    setError('');
    getMarketingPreference(restaurantId, customerId).then((result) => {
      if (!result?.success) throw new Error('Your message preferences could not be loaded.');
      if (active) {
        setAllowed(result.data.allowed);
        setSaved(result.data.allowed);
      }
    }).catch(() => {
      if (active) setError('Your message preferences could not be loaded. Please try again.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [restaurantId, customerId, isGuest, attempt]);

  const save = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const result = await setMarketingPreference(restaurantId, customerId, allowed);
      if (!result?.success) throw new Error('Could not save');
      setSaved(result.data.allowed);
      setNotice(result.data.allowed ? 'You will receive offers and updates.' : 'Offers and updates are now turned off. Messages about your orders and bookings will still reach you.');
    } catch {
      setError('Your change was not saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="py-4" aria-labelledby="messages-title">
      <h2 id="messages-title" className="text-base font-semibold text-gray-900">Messages from {restaurantName || 'this restaurant'}</h2>
      {isGuest ? (
        <div className="mt-3 space-y-4">
          <p className="text-sm leading-6 text-gray-600">Sign in to manage offers and updates. You can also ask the restaurant to stop them.</p>
          <button onClick={onSignIn} className="min-h-[44px] rounded-md px-5 text-sm font-semibold text-white" style={{ backgroundColor: accentColor }}>Sign in</button>
        </div>
      ) : !customerId || !restaurantId ? <p className="mt-3 text-sm leading-6 text-gray-600">Your message preference will be available once your account is linked to this restaurant. You can also contact the restaurant to stop offers.</p>
        : loading ? <p className="py-5 text-sm text-gray-500" role="status">Loading your preference...</p> : (
        <div className="mt-4 space-y-4">
          {saved !== null && <>
            <label className="flex cursor-pointer items-start gap-3 py-2">
              <input type="checkbox" checked={allowed} disabled={saving} onChange={(event) => { setAllowed(event.target.checked); setNotice(''); }}
                className="mt-1 h-5 w-5 shrink-0 rounded border-gray-300" style={{ accentColor }} />
              <span className="text-sm font-medium leading-6 text-gray-900">Send me offers and updates from {restaurantName || 'this restaurant'}.</span>
            </label>
            <p className="text-sm leading-6 text-gray-500">This only affects offers and updates, not messages about your orders or reservations.</p>
            <button onClick={save} disabled={saving || allowed === saved} className="min-h-[44px] rounded-md px-5 text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: accentColor }}>
              {saving ? 'Saving...' : 'Save preference'}
            </button>
          </>}
          {error && <div role="alert" className="space-y-2 text-sm text-red-700"><p>{error}</p>{saved === null && <button className="min-h-[44px] font-semibold underline" onClick={() => setAttempt((value) => value + 1)}>Try again</button>}</div>}
          {notice && <p role="status" className="text-sm leading-6 text-gray-700">{notice}</p>}
        </div>
      )}
    </section>
  );
}
